import {
  KatButton,
  KatDropdown,
  KatIcon,
  KatInput,
  KatLabel,
  KatModal,
} from "@amzn/katal-react";
import classnames from "classnames";
import React, { type FC, useState } from "react";
import { useTranslation } from "react-i18next";
import CopyToClipboardButton from "src/components/Pages/DocumentInbound/CopyToClipboardButton";
import {
  DEFAULT_TRANSLATION_CONFIG,
  DEFAULT_TRANSLATION_NS,
} from "src/components/TranslatedField/TranslationKey";
import { ComplianceUserActionType } from "src/components/UserActions/types";
import publishMetric, { MetricAction } from "src/metrics";
import styles from "src/styles.module.scss";

export type ConfirmableActionType =
  | ComplianceUserActionType.FORCE_REJECT
  | ComplianceUserActionType.FORCE_ACCEPT;

export interface ConfirmationActionMetadata {
  readonly titleKey?: string;
  readonly mainTextKey?: string;
  readonly warningKey?: string;
}

export interface ConfirmationAction {
  readonly type: ConfirmableActionType;
  readonly metadata?: ConfirmationActionMetadata;
}

export interface ForceActionConfirmationModalProps {
  actionType: ConfirmableActionType;
  onClose: () => void;
  handleAction: any;
  documentId: string;
  // Reason translation keys offered in a dropdown. Only shown for actions
  // with a `reason` config; the selected key is sent to handleAction.
  reasons?: string[];
  // Optional translation key overrides for the action.
  actionMetadata?: ConfirmationActionMetadata;
}

interface ReasonConfig {
  // Key in the user actions metadata holding the reasons list.
  metadataKey: string;
  // Key used to send the selected reason to handleAction.
  payloadKey: string;
  subtextKey: string;
  placeholderKey: string;
}

interface ActionConfig {
  defaultTitleKey: string;
  defaultMainTextKey: string;
  defaultWarningKey: string;
  actionLabelKey: string;
  confirmMetric: MetricAction;
  cancelMetric: MetricAction;
  confirmButtonVariant: KatButton.Variant;
  cancelButtonVariant: KatButton.Variant;
  requireIdConfirmation: boolean;
  reason?: ReasonConfig;
}

const ACTION_CONFIG: Record<ConfirmableActionType, ActionConfig> = {
  [ComplianceUserActionType.FORCE_REJECT]: {
    defaultTitleKey: "aic_doc_page_force_rejection_modal_title",
    defaultMainTextKey: "aic_doc_page_force_rejection_modal_main_text",
    defaultWarningKey: "aic_doc_page_force_rejection_modal_warning",
    actionLabelKey: "aic_doc_page_force_rejection_modal_action",
    confirmMetric: MetricAction.FORCE_REJECTION_CONFIRM,
    cancelMetric: MetricAction.FORCE_REJECTION_CANCEL,
    confirmButtonVariant: "danger",
    cancelButtonVariant: "primary",
    requireIdConfirmation: true,
    reason: {
      metadataKey: "rejectionReasons",
      payloadKey: "rejectionReasonKey",
      subtextKey: "aic_doc_page_force_rejection_modal_rejection_reason_subtext",
      placeholderKey: "aic_doc_page_force_rejection_rejection_reason",
    },
  },
  [ComplianceUserActionType.FORCE_ACCEPT]: {
    defaultTitleKey: "aic_doc_page_force_accept_modal_title",
    defaultMainTextKey: "aic_doc_page_force_accept_modal_main_text",
    defaultWarningKey: "aic_doc_page_force_accept_modal_warning",
    actionLabelKey: "aic_doc_page_force_accept_modal_action",
    confirmMetric: MetricAction.FORCE_ACCEPT_CONFIRM,
    cancelMetric: MetricAction.FORCE_ACCEPT_CANCEL,
    // Cancel stays emphasized (primary) like in Force Reject to discourage
    // the action; confirm uses secondary so the two buttons differ.
    confirmButtonVariant: "secondary",
    cancelButtonVariant: "primary",
    requireIdConfirmation: false,
  },
};

export const isConfirmableActionType = (
  action: string
): action is ConfirmableActionType =>
  Object.prototype.hasOwnProperty.call(ACTION_CONFIG, action);

export const getReasonsMetadataKey = (
  actionType: ConfirmableActionType
): string | undefined => ACTION_CONFIG[actionType].reason?.metadataKey;

const ForceActionConfirmationModal: FC<ForceActionConfirmationModalProps> = ({
  actionType,
  handleAction,
  onClose,
  documentId,
  reasons = [],
  actionMetadata,
}) => {
  const config = ACTION_CONFIG[actionType];
  const reasonConfig = config.reason;

  const [confirmationId, setConfirmationId] = useState("");
  const [reasonKey, setReasonKey] = useState("");
  const { t } = useTranslation(
    DEFAULT_TRANSLATION_NS,
    DEFAULT_TRANSLATION_CONFIG
  );

  const titleKey = actionMetadata?.titleKey ?? config.defaultTitleKey;

  const mainTextKey = actionMetadata?.mainTextKey ?? config.defaultMainTextKey;

  const warningKey = actionMetadata?.warningKey ?? config.defaultWarningKey;

  const reasonsDropdownItems = reasonConfig
    ? reasons.map(
        (reason) =>
          ({
            name: t(reason),
            value: reason,
          }) as KatalDropdownOption
      )
    : [];

  const isReasonEnabled = reasonsDropdownItems.length !== 0;

  const handleOnChange = ({ target: { value } }: any) => {
    setConfirmationId(value.trim());
  };

  const handleOnChangeReasonKey = ({ target: { value } }: any) => {
    setReasonKey(value.trim());
  };

  const handleCancel = () => {
    publishMetric(config.cancelMetric);
    setConfirmationId("");
    onClose();
  };

  function handleConfirm() {
    publishMetric(config.confirmMetric);
    onClose();
    if (reasonConfig && isReasonEnabled) {
      handleAction(actionType, {
        [reasonConfig.payloadKey]: reasonKey,
      });
      return;
    }
    handleAction(actionType);
  }

  const isIdConfirmed =
    !config.requireIdConfirmation || documentId === confirmationId;
  const isReasonSelected = !isReasonEnabled || reasonKey !== "";
  const isConfirmAllowed = isIdConfirmed && isReasonSelected;

  return (
    <KatModal visible={true} title={t(titleKey)} onClose={() => handleCancel()}>
      <div
        className={classnames(
          styles.flex_row_nonwrap,
          styles.pb_8,
          styles.relative
        )}
      >
        <KatLabel
          text={t(mainTextKey, {
            documentId: documentId,
          })}
          variant={"constraint"}
          className={classnames(styles.mr_08)}
        />
        <CopyToClipboardButton
          label="document-id"
          value={documentId}
          translationPrefix="aic_doc_page_breadcrumb_document_list_document_id_popover"
        />
        <KatLabel text={"?"} variant={"constraint"} />
      </div>

      <div className={classnames(styles.flex_direction_row, styles.pb_8)}>
        <KatIcon name={"alert-fill"} size={"tiny"} />
        <KatLabel text={t(warningKey)} />
      </div>

      {reasonConfig && isReasonEnabled && (
        <div>
          <KatLabel text={t(reasonConfig.subtextKey)} variant={"constraint"} />
          <KatDropdown
            placeholder={t(reasonConfig.placeholderKey)}
            options={reasonsDropdownItems}
            onChange={handleOnChangeReasonKey}
          />
          <br></br>
        </div>
      )}

      {config.requireIdConfirmation && (
        <>
          <KatLabel
            text={t("aic_doc_page_force_rejection_modal_subtext")}
            variant={"constraint"}
          />
          <KatInput
            value={confirmationId}
            id={"input"}
            onInput={handleOnChange}
            type="text"
            placeholder={t("aic_doc_page_force_rejection_doc_id")}
            className={classnames(styles.pt_8)}
          />
        </>
      )}

      <div className={classnames(styles.buttons_at_the_end)}>
        <KatButton
          variant={config.confirmButtonVariant}
          onClick={() => handleConfirm()}
          disabled={!isConfirmAllowed}
        >
          {t(config.actionLabelKey)}
        </KatButton>

        <KatButton
          variant={config.cancelButtonVariant}
          onClick={() => handleCancel()}
        >
          {t("aic_doc_page_po_header_edit_cancel")}
        </KatButton>
      </div>
    </KatModal>
  );
};

export default ForceActionConfirmationModal;
